import React from 'react';
import { connect } from 'react-redux';

import {
  Button, Switch, Form, Modal,
  ButtonVariant, Text, Flex, FlexItem, Grid, GridItem,
} from '@patternfly/react-core';
import DatePicker from 'react-datepicker';

import LoadingSpinner from 'ui/components/LoadingSpinner';
import { Shift } from 'domain/Shift';
import { Spot } from 'domain/Spot';
import { Employee } from 'domain/Employee';
import { AppState } from 'store/types';
import { getAvailableEmployees, getAvailableEmployeesForNewShift } from 'store/shift/operations';
import { spotSelectors } from 'store/spot';
import { withTranslation, WithTranslation } from 'react-i18next';

import TypeaheadSelectInput from 'ui/components/TypeaheadSelectInput';
import { skillSelectors } from 'store/skill';
import { Skill } from 'domain/Skill';
import moment from 'moment';

interface Props {
  tenantId: number;
  shift?: Shift;
  isOpen: boolean;
  skillList: Skill[];
  // employeeList: Employee[];
  // activeEmployeeList: Employee[];
  spotList: Spot[];
  // eslint-disable-next-line no-unused-vars
  onSave: (shift: Shift) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (shift: Shift) => void;
  onClose: () => void;
  loadAvailableEmployees: (
    tenantId: number,
    shiftId: number,
    allowOvertime: boolean
  ) => Promise<Employee[]>;
  loadAvailableEmployeesForNewShift: (
    shift: Shift,
    allowOvertime: boolean
  ) => Promise<Employee[]>;
}

const mapStateToProps = (state: AppState, ownProps: {
  shift?: Shift;
  isOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  onSave: (shift: Shift) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (shift: Shift) => void;
  onClose: () => void;
}): Omit<Props, "loadAvailableEmployees" | "loadAvailableEmployeesForNewShift"> => ({
  ...ownProps,
  tenantId: state.tenantData.currentTenantId,
  skillList: skillSelectors.getSkillList(state),
  // employeeList: employeeSelectors.getEmployeeList(state), // TODOS
  // activeEmployeeList: employeeSelectors.getActiveEmployeeList(state), // SOLO activos
  spotList: spotSelectors.getSpotList(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  loadAvailableEmployees: (
    tenantId: number,
    shiftId: number,
    allowOvertime: boolean
  ) => dispatch(getAvailableEmployees(tenantId, shiftId, allowOvertime)),
  loadAvailableEmployeesForNewShift: (
    shift: Shift,
    allowOvertime: boolean
  ) => dispatch(
    getAvailableEmployeesForNewShift(shift, allowOvertime)
  ),
});

interface State {
  resetCount: number;
  editedValue: Partial<Shift>;
  isValid: boolean;
  isLoading: boolean;
  availableEmployees: Employee[];
  allowOvertime: boolean;
}

export class EditShiftModal extends React.Component<Props & WithTranslation, State> {
  private availabilityTimer?: ReturnType<typeof setTimeout>;
private availabilityRequestId = 0;
  constructor(props: Props & WithTranslation) {
    super(props);

    this.onSave = this.onSave.bind(this);
    this.validateShift = this.validateShift.bind(this);

    this.state = {
      resetCount: 0,
      editedValue: props.shift ? { ...props.shift } : {
        tenantId: props.tenantId,
        employee: null,
        originalEmployee: null,
        requiredSkillSet: [],
        rotationEmployee: null,
        pinnedByUser: false,
      },
      isValid: true,
      isLoading: false,
      availableEmployees: [],
      allowOvertime: false,
    };
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
  if (
    this.props.shift === undefined &&
    prevProps.shift !== undefined
  ) {
    this.invalidateAvailabilityRequests();

    this.setState({
      resetCount: prevState.resetCount + 1,
      editedValue: {
        tenantId: this.props.tenantId,
        employee: null,
        originalEmployee: null,
        requiredSkillSet: [],
        rotationEmployee: null,
        pinnedByUser: false,
      },
      availableEmployees: [],
      isLoading: false,
    }, this.validateShift);

    return;
  }

  if (
    this.props.shift !== undefined &&
    (
      this.props.shift?.id !== prevProps.shift?.id ||
      this.props.shift?.version !== prevProps.shift?.version
    )
  ) {
    this.invalidateAvailabilityRequests();

    this.setState({
      resetCount: prevState.resetCount + 1,
      editedValue: this.props.shift,
      availableEmployees: [],
    }, () => {
      this.validateShift();
      this.scheduleAvailableEmployeesLoad();
    });

    return;
  }

  if (!prevProps.isOpen && this.props.isOpen) {
    this.scheduleAvailableEmployeesLoad();
    return;
  }

  const previous = prevState.editedValue;
  const current = this.state.editedValue;

  const availabilityFieldsChanged =
    previous.spot?.id !== current.spot?.id ||
    previous.startDateTime?.getTime() !== current.startDateTime?.getTime() ||
    previous.endDateTime?.getTime() !== current.endDateTime?.getTime() ||
    prevState.allowOvertime !== this.state.allowOvertime;

  if (this.props.isOpen && availabilityFieldsChanged) {
    if (!this.props.shift?.id && this.state.editedValue.employee) {
      this.setState((prevState) => ({
        editedValue: {
          ...prevState.editedValue,
          employee: null,
        },
      }));
    }

    this.scheduleAvailableEmployeesLoad();
  }

  if (prevProps.isOpen && !this.props.isOpen) {
    this.invalidateAvailabilityRequests();
  }
}

componentWillUnmount() {
  this.invalidateAvailabilityRequests();
}

  validateShift() {
    
    const { editedValue } = this.state;
    let isValid = true;

    const errorMessages: string[] = [];

    if (!editedValue.startDateTime) {
      isValid = false;
      errorMessages.push(this.props.t('noStartTime'));
    }

    if (!editedValue.endDateTime) {
      isValid = false;
      errorMessages.push(this.props.t('noEndTime'));
    }

    if (editedValue.spot === undefined) {
      isValid = false;
      errorMessages.push(this.props.t('noSpot'));
    }

    if (editedValue.startDateTime && editedValue.endDateTime) {
      const duration = moment(editedValue.endDateTime).diff(editedValue.startDateTime, 'minutes');
      if (duration < 30) {
        isValid = false;
        errorMessages.push(this.props.t('belowMinDuration'));
      }
    }

    this.setState({ isValid });
    }

  private invalidateAvailabilityRequests = () => {
  this.availabilityRequestId++;

  if (this.availabilityTimer) {
    clearTimeout(this.availabilityTimer);
    this.availabilityTimer = undefined;
  }
};

private scheduleAvailableEmployeesLoad = () => {
  this.invalidateAvailabilityRequests();

  const { editedValue } = this.state;

  const start = editedValue.startDateTime;
  const end = editedValue.endDateTime;

  const valid =
    !!editedValue.spot &&
    !!start &&
    !!end &&
    moment(end).diff(moment(start), 'minutes') >= 30;

  if (!valid) {
    this.setState({
      availableEmployees: [],
      isLoading: false,
    });
    return;
  }

  this.setState({
    availableEmployees: [],
    isLoading: true,
  });

  this.availabilityTimer = setTimeout(() => {
    this.loadAvailableEmployees();
  }, 400);
};

  private async loadAvailableEmployees() {
    const requestId = ++this.availabilityRequestId;

    const { editedValue, allowOvertime } = this.state;
    const { tenantId } = this.props;

    try {
      const employees = await this.props.loadAvailableEmployeesForNewShift(
        {
          ...editedValue,
          tenantId,
        } as Shift,
        allowOvertime
      );

      if (requestId !== this.availabilityRequestId) return;

      // Obtener el Spot actualizado desde Redux
      const selectedSpot = this.props.spotList.find(
        (spot) => spot.id === editedValue.spot?.id
      );

      // Identificadores de empleados bloqueados para este Spot
      const blockedEmployeeIds = new Set(
        (selectedSpot?.blockedEmployeeSet || [])
          .filter((employee) => employee != null)
          .map((employee) => employee.id)
      );

      // Mantener las validaciones actuales y excluir bloqueados
      const filteredEmployees = (employees ?? []).filter(
        (employee) =>
          employee != null &&
          !blockedEmployeeIds.has(employee.id)
      );

      this.setState({
        availableEmployees: filteredEmployees,
        isLoading: false,
      });

    } catch (error) {
      if (requestId !== this.availabilityRequestId) return;

      console.error('Error al consultar empleados disponibles:', error);

      this.setState({
        availableEmployees: [],
        isLoading: false,
      });
    }
  }

  async onSave() {
    const { editedValue } = this.state;
    // this.setState({ isLoading: true });
    if (this.state.isValid) {
      try {
        await this.props.onSave({
          ...editedValue,
          tenantId: this.props.tenantId,
          originalEmployee: this.props.shift ? this.props.shift.originalEmployee : null,
        } as Shift);
      } finally {
        // this.setState({ isLoading: false });
      }
    }
  }

  render() {
    const { t } = this.props;
    const dateFormat = 'MMMM dd, hh:mm a';

    return (
      <Modal
        title={this.props.shift ? t('editShift') : t('createShift')}
        onClose={this.props.onClose}
        isOpen={this.props.isOpen}
        actions={
          [
            <Button
              aria-label="Close Modal"
              variant={ButtonVariant.tertiary}
              key={0}
              onClick={this.props.onClose}
            >
              {t('close')}
            </Button>,
          ].concat(this.props.shift ? [
            <Button
              aria-label="Delete"
              variant={ButtonVariant.danger}
              key={1}
              onClick={() => this.props.onDelete(this.props.shift as Shift)}
            >
              {t('delete')}
            </Button>,
          ] : []).concat([
            <Button
              aria-label="Save"
              key={2}
              onClick={this.onSave}
              isDisabled={!this.state.isValid}
            >
              {t('save')}
            </Button>,
          ])
        }
        variant="small"
        width={750}
      >
        {/* {this.state.isLoading ? <LoadingSpinner isLoading /> : ( */}
          <Form id="modal-element" key={this.state.resetCount} onSubmit={(e) => e.preventDefault()}>
            <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>{t('spot')}</Text>
                  </GridItem>
                  <GridItem span={8}>
                    <TypeaheadSelectInput
                      aria-label="Spot"
                      emptyText={t('selectSpot')}
                      value={this.state.editedValue.spot}
                      options={this.props.spotList}
                      optionToStringMap={(spot) => spot.name}
                      onChange={(spot) => {
                        this.setState((prevState) => {

                          let startDateTime = prevState.editedValue.startDateTime;
                          let endDateTime = prevState.editedValue.endDateTime;

                          if (spot && spot.isApartment && !prevState.editedValue.startDateTime) {
                            const now = moment();

                            startDateTime = now.clone()
                              .hour(11)
                              .minute(0)
                              .second(0)
                              .millisecond(0)
                              .toDate();

                            endDateTime = now.clone()
                              .hour(15)
                              .minute(0)
                              .second(0)
                              .millisecond(0)
                              .toDate();
                          }

                          return {
                            editedValue: {
                              ...prevState.editedValue,
                              spot,
                              startDateTime,
                              endDateTime,
                            }
                          };

                        }, this.validateShift);
                      }}
                    />
                  </GridItem>
                </Grid>
              </FlexItem>
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>{t('shiftStart')}</Text>
                  </GridItem>
                  <GridItem span={8}>
                    <DatePicker
                      aria-label="Shift Start"
                      selected={this.state.editedValue.startDateTime}
                      onChange={(date) => this.setState((prevState) => ({
                        editedValue: { ...prevState.editedValue, startDateTime: (date !== null) ? date : undefined },
                      }), this.validateShift)}
                      dateFormat={dateFormat}
                      showTimeSelect
                    />
                  </GridItem>
                </Grid>
              </FlexItem>
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>{t('shiftEnd')}</Text>
                  </GridItem>
                  <GridItem span={8}>
                    <DatePicker
                      aria-label="Shift End"
                      selected={this.state.editedValue.endDateTime}
                      onChange={(date) => this.setState((prevState) => ({
                        editedValue: { ...prevState.editedValue, endDateTime: (date !== null) ? date : undefined },
                      }), this.validateShift)}
                      dateFormat={dateFormat}
                      showTimeSelect
                    />
                  </GridItem>
                </Grid>
              </FlexItem>
           
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>Hora extra</Text>
                  </GridItem>

                  <GridItem span={8}>
                    <Switch
                      id="allowOvertime"
                      aria-label="Hora extra"
                      isChecked={this.state.allowOvertime}
                      onChange={(checked) =>
                        this.setState({ allowOvertime: checked })
                      }
                    />
                  </GridItem>
                </Grid>
              </FlexItem>
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>{t('employee')}</Text>
                  </GridItem>
                  <GridItem span={8}>
                    {this.state.isLoading ? (
                      <LoadingSpinner isLoading />
                    ) : (
                      <TypeaheadSelectInput
                        aria-label="Employee"
                        emptyText={t('unassigned')}
                        value={(this.state.editedValue.employee !== null)
                          ? this.state.editedValue.employee : undefined}
                        options={this.state.availableEmployees}
                        optionToStringMap={(employee) => (employee ? employee.name : t('unassigned'))}
                        onChange={(employee) => this.setState((prevState) => ({
                          editedValue: { ...prevState.editedValue, employee: (employee !== undefined) ? employee : null },
                        }))}
                        optional
                      />
                     )}
                  </GridItem>
                </Grid>
              </FlexItem>
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>{t('originalEmployee')}</Text>
                  </GridItem>
                  <GridItem span={8}>
                    <Text>
                      {
                        this.state.editedValue.originalEmployee
                          ? this.state.editedValue.originalEmployee.name : t('unassigned')
                      }
                    </Text>
                  </GridItem>
                </Grid>
              </FlexItem>
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>{t('rotationEmployee')}</Text>
                  </GridItem>
                  <GridItem span={8}>
                 
                    <Text>
                      {
                        this.state.editedValue.rotationEmployee
                          ? this.state.editedValue.rotationEmployee.name : t('unassigned')
                      }
                    </Text>
                  </GridItem>
                </Grid>
              </FlexItem>
              <FlexItem>
                <Grid>
                  <GridItem span={4}>
                    <Text>{t('isPinned')}</Text>
                  </GridItem>
                  <GridItem span={8}>
                    <Switch
                      id="isPinned"
                      aria-label="Is Pinned"
                      isChecked={this.state.editedValue.pinnedByUser}
                      onChange={(isPinned) => this.setState((prevState) => ({
                        editedValue: { ...prevState.editedValue, pinnedByUser: isPinned },
                      }))}
                    />
                  </GridItem>
                </Grid>
              </FlexItem>
            </Flex>
          </Form>
        {/* // )} */}
      </Modal>
    );
  }
}

export default withTranslation('EditShiftModal')(
  connect(mapStateToProps,mapDispatchToProps)(EditShiftModal),
);
